import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { genSchoolInstituteCode, genSectionCode } from '../services/client.service';
import PoweredByDQLogo from '../assets/images/PoweredByDQ_Default.png';
import {
    TEMPLATE_SCHOOL_CLASS, TEMPLATE_NETWORK_CLASS, detectImportTemplate, readClassImportRows,
    fillImportTemplate, loadTemplateLookup, countryNameFor, stateForExport,
} from './importTemplates';

/** Brand palette for the downloaded template — navy only, per request (no
 *  orange/teal accents). Navy is the app's own `--brand-secondary` token (see
 *  buildTheme.js / branding.context.jsx). A single lighter navy tint stands
 *  in for what used to be the warm example-row/zebra-row fill and the teal
 *  divider, so every accent in the sheet reads as "the same blue," not a mix.
 *  exceljs ARGB requires the leading alpha byte, hence 'FF' + hex. */
const BRAND_NAVY = 'FF052F44';
const BRAND_NAVY_TINT = 'FFF3F6F8'; // faint cool tint — example rows, zebra striping, divider
const HEADER_TEXT = 'FFFFFFFF';
const EXAMPLE_ROW_TEXT = 'FF4B5563'; // dark-enough gray to stay legible; italic still marks it as placeholder data
const EXAMPLE_ROW_FILL = BRAND_NAVY_TINT;
const ZEBRA_ROW_FILL = BRAND_NAVY_TINT; // alternating tint for real data rows in long exports
const THIN_BORDER = { style: 'thin', color: { argb: 'FFE5E7EB' } };
const CELL_BORDER = { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };

/** The logo is fetched once per session and reused across every worksheet/
 *  workbook — `workbook.addImage` registers image bytes at the WORKBOOK
 *  level, so the same registration can be attached to any number of
 *  worksheets without re-fetching. Falls back to null (banner just skips the
 *  image) if the fetch ever fails — never blocks generating the file. */
let _cachedLogoBuffer;
const _getLogoBuffer = async () => {
    if (_cachedLogoBuffer !== undefined) return _cachedLogoBuffer;
    try {
        const res = await fetch(PoweredByDQLogo);
        _cachedLogoBuffer = await res.arrayBuffer();
    } catch (_) {
        _cachedLogoBuffer = null;
    }
    return _cachedLogoBuffer;
};

/**
 * Add the branded letterhead — logo, a navy title banner, and a thin teal
 * accent divider — as the first 3 rows of a worksheet. Returns the number of
 * rows it used, so the caller knows where its own header row starts.
 * @param {import('exceljs').Workbook} wb
 * @param {import('exceljs').Worksheet} ws
 * @param {string} title
 * @param {number} totalCols
 * @param {number|null} logoImageId From `wb.addImage(...)`, or null to skip the logo.
 * @returns {number} banner row count (3)
 */
const _addBrandBanner = (wb, ws, title, totalCols, logoImageId) => {
    const cols = Math.max(totalCols, 1);

    // Row 1 — logo, in its own quiet row with a soft background.
    ws.addRow([]);
    const logoRow = ws.getRow(1);
    logoRow.height = 40;
    for (let c = 1; c <= cols; c += 1) {
        ws.getCell(1, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
    }
    if (logoImageId != null) {
        // Source is 1196×512 (~2.34:1) — scaled down to letterhead size.
        ws.addImage(logoImageId, {
            tl: { col: 0.15, row: 0.15 },
            ext: { width: 130, height: 56 },
        });
    }

    // Row 2 — title banner: bold navy text on a light navy tint, merged across
    // every column — a quieter letterhead look than solid navy, and it keeps
    // the title visually distinct from the solid-navy table headers below it.
    ws.addRow([title]);
    const titleRow = ws.getRow(2);
    titleRow.height = 26;
    ws.mergeCells(2, 1, 2, cols);
    const titleCell = ws.getCell(2, 1);
    titleCell.font = { bold: true, size: 13, color: { argb: BRAND_NAVY } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_TINT } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

    // Row 3 — thin tint divider, same fill as the title so rows 1-3 read as
    // one quiet letterhead band before the first solid-navy header below it.
    ws.addRow([]);
    ws.getRow(3).height = 5;
    for (let c = 1; c <= cols; c += 1) {
        ws.getCell(3, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_TINT } };
    }

    return 3;
};

/**
 * The audience a student uploaded from a file is given when the row names none
 * (the approved template has no Audience Type column). Students are the
 * school's "YoungPeople" audience, so that group is used whenever the school
 * has it; a school without it falls back to its first group, as before.
 */
export const pickDefaultStudentUserGroup = (groups) => {
    const list = (Array.isArray(groups) ? groups : []).map((g) => String(g || '').trim()).filter(Boolean);
    return list.find((g) => g.toLowerCase() === 'youngpeople') || list[0] || '';
};

/** Mirrors `ORG_TYPE` in my-dq-api `utils/constants` */
export const ORG_TYPE_SCHOOL = 3;
export const ORG_TYPE_COMPANY = 2;

/**
 * Older downloaded templates (before this file switched to real exceljs cell
 * styling) faked bold header text using Unicode "Mathematical Sans-Serif
 * Bold" letters/digits — different codepoints that simply render bold in any
 * viewer, since plain xlsx/csv can't carry real font styling. Every current
 * generator here uses actual bold font styling instead, but a re-uploaded
 * OLDER template may still carry these codepoints in its header row, so
 * parsing still normalizes them back to plain ASCII before matching.
 */
const _BOLD_UPPER_BASE = 0x1D5D4; // Mathematical Sans-Serif Bold Capital A
const _BOLD_LOWER_BASE = 0x1D5EE; // Mathematical Sans-Serif Bold Small A
const _BOLD_DIGIT_BASE = 0x1D7EC; // Mathematical Sans-Serif Bold Digit Zero

const _fromBoldUnicode = (str) => {
    let out = '';
    for (const ch of String(str ?? '')) {
        const code = ch.codePointAt(0);
        if (code >= _BOLD_UPPER_BASE && code < _BOLD_UPPER_BASE + 26) {
            out += String.fromCharCode(65 + (code - _BOLD_UPPER_BASE));
        } else if (code >= _BOLD_LOWER_BASE && code < _BOLD_LOWER_BASE + 26) {
            out += String.fromCharCode(97 + (code - _BOLD_LOWER_BASE));
        } else if (code >= _BOLD_DIGIT_BASE && code < _BOLD_DIGIT_BASE + 10) {
            out += String.fromCharCode(48 + (code - _BOLD_DIGIT_BASE));
        } else {
            out += ch;
        }
    }
    return out;
};

const SCHOOL_LICENSE_HEADERS = [
    'class_name',
    'grade_level',
    'first_name',
    'last_name',
    'email_address',
    'license_type',
    'user_group',
    'gender',
    'country', // PBDQ-45 — teacher-set learner location
    'state',
    'birthdate',
];

const COMPANY_LICENSE_HEADERS = [
    'department',
    'job_title',
    'first_name',
    'last_name',
    'email_address',
    'license_type',
    'user_group',
    'gender',
    'birthdate',
];

const EMPLOYEES_CSV_COLUMN_NAMES = [
    'First Name',
    'Last Name',
    'Department',
    'Job Title',
    'Birthdate (DD/MM/YYYY)',
    'Email Address',
    'Gender (Male /Female /Other)',
    'Username (Optional / Required to Update)',
];

/** The create-mode template splits into two blocks: a one-time "class info"
 *  label+value pair (this array) shared by every student in the file, and a
 *  per-student table below it (STUDENT_COLUMN_NAMES) — instead of repeating
 *  the class/teacher fields on every single row. `headerToJsonKey` still maps
 *  all of these labels regardless of which block they sit in, so an OLDER
 *  downloaded template (single flat table, these columns per-row) still
 *  re-uploads without error — parseClassInfoBlockFromAoa only fills a value
 *  that a row doesn't already carry itself. */
const CLASS_INFO_COLUMN_NAMES = [
    'Class Name',
    'Grade Level',
    'Academic Year',
    'Teacher First Name',
    'Teacher Last Name',
    'Teacher Email',
];

const CLASS_INFO_EXAMPLE_ROW = ['IT', 'Grade A', '2025-2026', 'Jasmine', 'Wood', 'teacher0@dq.com'];

/** The student table of the approved upload template (SW_template.xlsx,
 *  4 Oct 2026) — these ten columns, in this order, with these labels (Unicode
 *  apostrophe in parent’s). City is new; Postal Code moved to the end.
 *  Gender, Country, State / Province / Region, City and Postal Code are optional. An older
 *  downloaded template still uploads: columns are read by their header, not
 *  their position (see headerToJsonKey). */
const STUDENT_COLUMN_NAMES = [
    'First Name',
    'Last Name',
    'Year of Birth (YYYY)',
    'Learner Email',
    'Parent Email',
    'Gender (Male /Female /Other)',
    'Country',
    'State / Province / Region',
    'City',
    'Postal Code',
];

/** The sheet the approved template puts a class on. More classes go on further
 *  sheets of the same shape ("Class 2", …) — every sheet is read on upload. */
const CLASS_SHEET_NAME = 'Class 1';

// Aligned 1:1 with STUDENT_COLUMN_NAMES's order — then any trailing `extra`
// columns (License type, ...) appended by buildLicenseHeaderNames.
const STUDENT_EXAMPLE_V2 = [
    'Adam',
    'Mike',
    '2011',
    'mike@dq.com',
    '',
    'Male',
    'USA',
    'CA',
    'Los Angeles',
    '90001',
];

/** Username column left blank like the portal (Postal Code removed with the learner columns). */
const EMPLOYEE_EXAMPLE_BASE = ['Adam', 'Mike', 'IT', 'Manager', '15/10/2002', 'mike@dq.com', 'male'];

const padRow = (cells, len) => {
    const r = Array.isArray(cells) ? cells.slice() : [];
    while (r.length < len) r.push('');
    return r;
};

export const getExportColumnKeys = (orgType) =>
    orgType === ORG_TYPE_COMPANY ? [...COMPANY_LICENSE_HEADERS] : [...SCHOOL_LICENSE_HEADERS];

/** Bold white-on-navy row styling shared by every table header in these
 *  workbooks — factored out so a sheet with several repeated headers (one per
 *  class, see buildExistingUsersWorkbook) can style each one identically
 *  without going through the rest of _styleTemplateWorksheet (freeze view,
 *  column widths) every time. */
const _styleNavyHeaderRow = (ws, rowNum) => {
    const row = ws.getRow(rowNum);
    row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        cell.border = CELL_BORDER;
    });
    row.height = 24;
};

/**
 * Style one already-populated worksheet the way every downloaded template
 * should look: a bold white-on-brand-navy header row (frozen so it stays
 * visible while scrolling), thin borders on every populated cell, example
 * rows shown in muted italic text on a faint tint so they read as
 * placeholders rather than real data, and auto-fit column widths.
 * @param {import('exceljs').Worksheet} ws
 * @param {string[]} headers
 * @param {string[][]} exampleRows Rows to render in the muted "placeholder" style.
 * @param {number} [headerRowNum] 1-based row the column headers sit on — the
 *   branded banner (see _addBrandBanner) occupies the rows above it.
 */
const _styleTemplateWorksheet = (ws, headers, exampleRows, headerRowNum = 1) => {
    _styleNavyHeaderRow(ws, headerRowNum);
    ws.views = [{ state: 'frozen', ySplit: headerRowNum }];

    for (let i = 0; i < exampleRows.length; i += 1) {
        const row = ws.getRow(headerRowNum + i + 1);
        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.font = { italic: true, color: { argb: EXAMPLE_ROW_TEXT }, size: 10.5 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXAMPLE_ROW_FILL } };
            cell.border = CELL_BORDER;
        });
        row.height = 20;
    }

    ws.columns.forEach((col, i) => {
        const header = String(headers[i] ?? '');
        let maxLen = header.length;
        for (const r of exampleRows) {
            maxLen = Math.max(maxLen, String(r[i] ?? '').length);
        }
        col.width = Math.min(Math.max(maxLen + 4, 14), 42);
    });
};

/**
 * Style a run of REAL data rows (not placeholders) — light zebra striping so
 * a long roster stays readable row-to-row, plus a thin border on every cell.
 */
const _styleDataRows = (ws, startRow, rowCount) => {
    for (let i = 0; i < rowCount; i += 1) {
        const row = ws.getRow(startRow + i);
        const isEven = i % 2 === 1;
        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.font = { size: 10.5, color: { argb: 'FF111827' } };
            if (isEven) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA_ROW_FILL } };
            }
            cell.border = CELL_BORDER;
        });
        row.height = 19;
    }
};

/** Style the small "head/institute info" (or class-info) label+value pair
 *  under the banner — same navy/white header treatment as the main student
 *  table header below it (_styleTemplateWorksheet), so the two header rows
 *  read as one consistent system rather than a strong header next to a
 *  washed-out secondary one.
 *  @param {boolean} [exampleStyle] The value row holds placeholder/example
 *   data (the upload template's class-info block) rather than real exported
 *   data (buildExistingUsersWorkbook's institute info) — when true, style it
 *   exactly like the student table's own example row (same tint fill, same
 *   muted italic text) so the two "value" rows visually match. */
const _styleHeadInfoSection = (ws, labelRowNum, exampleStyle = false) => {
    const labelRow = ws.getRow(labelRowNum);
    labelRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY } };
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        cell.border = CELL_BORDER;
    });
    labelRow.height = 22;
    const valueRow = ws.getRow(labelRowNum + 1);
    valueRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = exampleStyle
            ? { italic: true, color: { argb: EXAMPLE_ROW_TEXT }, size: 10.5 }
            : { size: 10.5, color: { argb: 'FF111827' } };
        if (exampleStyle) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXAMPLE_ROW_FILL } };
        }
        cell.border = CELL_BORDER;
    });
    valueRow.height = 20;
};

/**
 * Build upload template workbook (profiles_data.xlsx) as a real exceljs
 * workbook so the download can carry actual cell styling (bold/color/fill/
 * border) — the `xlsx` package used elsewhere in this file for reading
 * uploads can only do plain, unstyled worksheets (that's gated behind
 * SheetJS's paid tier). Now async: it fetches the brand logo for the
 * letterhead banner (see _addBrandBanner). Await `downloadExcelJsWorkbook`
 * on the result.
 * @param {string} [targetClassName] Pre-fill Class/Sub-Group in sample rows when set
 *   (ignored when isEnrollMode — see below).
 * @param {boolean} [isEnrollMode] Adding students to an already-existing class —
 *   returns a minimal, dedicated template (see the branch below) instead of the
 *   full create-mode one.
 * @returns {Promise<import('exceljs').Workbook>}
 */
export const buildStudentUploadTemplateWorkbook = async (licensesInfoData, orgType, targetClassName, prefillSchool = null, isEnrollMode = false) => {
    const isSchool = orgType === ORG_TYPE_SCHOOL;
    const wb = new ExcelJS.Workbook();
    const logoBuf = await _getLogoBuffer();
    const logoImageId = logoBuf ? wb.addImage({ buffer: logoBuf, extension: 'png' }) : null;

    // Adding students to an EXISTING class: "Manage Students" → table mode only
    // ever asks for these fields per student (see AddClassStudentsDialog's enroll
    // table headers) — the class name, grade, school level, teacher, gender,
    // country, license type, username, password, and audience type are all
    // already known from the class itself or optional/defaulted server-side, the
    // same way the manual table never collects them either. A template that still
    // carried the full create-mode column set — in particular a stale/placeholder
    // teacher in the "Class/Sub-Group Head ..." columns — is what caused a
    // re-upload to silently create a duplicate class, so this returns a genuinely
    // minimal template rather than trying to hide/override columns after parsing.
    // "Learner Email" / "Parent Email" are two separate columns here, matching the
    // table's two separate fields. parseStudentLicenseWorkbookToPostData maps them
    // the way the table path does: the PARENT's becomes email_address (the backend's
    // `profile.email`, which every consent pathway mails), and the learner's own
    // travels alongside as student_email.
    if (isSchool && isEnrollMode) {
        // "Year of Birth (YYYY)" matches the manual enroll table's own field
        // exactly (a Year of Birth dropdown, not a full date) — parsed via the
        // same year_of_birth_raw → birthdate conversion the create-mode
        // template also uses now (see yearOfBirthToBirthdate).
        // The same ten student columns as the class template, so one student
        // table is used everywhere.
        const headers = [...STUDENT_COLUMN_NAMES];
        const exampleRow = ['Adam', 'Mike', '2011', 'mike@dq.com', 'parent.mike@dq.com', 'Male', 'USA', 'CA', 'Los Angeles', '90001'];
        const ws = wb.addWorksheet('Students');
        const bannerRows = _addBrandBanner(wb, ws, 'DQ — Student Upload Template', headers.length, logoImageId);
        ws.addRow(headers);
        ws.addRow(exampleRow);
        _styleTemplateWorksheet(ws, headers, [exampleRow], bannerRows + 1);
        return wb;
    }

    const userGroups = Array.isArray(licensesInfoData?.user_group_list)
        ? licensesInfoData.user_group_list.filter(Boolean)
        : [];
    const profileAutoLink =
        licensesInfoData?.profile_auto_link?.enable === true &&
        licensesInfoData?.profile_auto_link?.strategy_name === 'domain';
    const buildLicenseHeaderNames = () => {
        const extra = [];
        /*
         * A SCHOOL's template is the approved one: the ten student columns and
         * nothing after them. "License (DQP/DQW/Both)" and "Audience Type" used
         * to be added here for schools with linked programs or audience groups;
         * the approved template has neither (6 Oct 2026). Nothing is lost on
         * upload — a student with no licence type is given DQP and one with no
         * audience the school's default group, as before — and a file that
         * still carries either column is read as it always was.
         */
        if (!isSchool && userGroups.length) {
            extra.push('Audience Type');
        }
        if (profileAutoLink) {
            extra.push('OpenID Email (optional)');
        }
        const base = isSchool ? STUDENT_COLUMN_NAMES : EMPLOYEES_CSV_COLUMN_NAMES;
        return [...base, ...extra];
    };

    const licenseNames = buildLicenseHeaderNames();
    const ncol = licenseNames.length;

    const sampleRows = [];
    if (isSchool) {
        // One example row, as in the approved template.
        sampleRows.push(padRow([...STUDENT_EXAMPLE_V2], ncol));
    } else {
        sampleRows.push(padRow([...EMPLOYEE_EXAMPLE_BASE], ncol));
    }

    // The school/head block (Head Teacher/Group Email, School/Group Name, ...) is
    // no longer included — this template is always downloaded from within one
    // specific school's own "Manage Classes" page (org_code already pins every
    // upload to that school server-side), so re-specifying the school per file
    // was always redundant here.
    const ws = wb.addWorksheet(isSchool ? CLASS_SHEET_NAME : 'Employees');

    if (isSchool) {
        // Class Name/Grade Level/Academic Year/Teacher fields apply to every
        // student in the file, so they're collected ONCE in a class-info block
        // above the student table instead of repeated on every row — separates
        // "class info" from "student info" visually and in the file itself.
        const classInfoRow = [...CLASS_INFO_EXAMPLE_ROW];
        // Looked up by name, not a hardcoded index — this column's position has
        // moved before, and a hardcoded index silently wrote the class name
        // into the wrong column when that happened.
        if (targetClassName) {
            const classNameIdx = CLASS_INFO_COLUMN_NAMES.indexOf('Class Name');
            if (classNameIdx >= 0) classInfoRow[classNameIdx] = String(targetClassName);
        }
        const totalCols = Math.max(CLASS_INFO_COLUMN_NAMES.length, licenseNames.length);
        const bannerRows = _addBrandBanner(
            wb, ws, 'DQ — Class with Students Upload Template', totalCols, logoImageId,
        );

        const classInfoLabelRow = bannerRows + 1;
        ws.addRow(CLASS_INFO_COLUMN_NAMES);
        ws.addRow(classInfoRow);
        _styleHeadInfoSection(ws, classInfoLabelRow, true);

        ws.addRow([]); // spacer

        const studentHeaderRow = classInfoLabelRow + 3;
        ws.addRow(licenseNames);
        for (const r of sampleRows) {
            ws.addRow(r);
        }
        _styleTemplateWorksheet(ws, licenseNames, sampleRows, studentHeaderRow);
        // _styleTemplateWorksheet only sized columns against the student
        // header/example rows — widen (never shrink) the leading columns
        // shared with the class-info block so its own labels/values fit too.
        ws.columns.forEach((col, i) => {
            const classLabel = CLASS_INFO_COLUMN_NAMES[i];
            if (!classLabel) return;
            const maxLen = Math.max(classLabel.length, String(classInfoRow[i] ?? '').length);
            col.width = Math.max(col.width || 0, Math.min(Math.max(maxLen + 4, 14), 42));
        });
        return wb;
    }

    // Employees — unchanged single flat table (no per-class grouping concept).
    const bannerRows = _addBrandBanner(wb, ws, 'DQ — Employee Upload Template', licenseNames.length, logoImageId);
    ws.addRow(licenseNames);
    for (const r of sampleRows) {
        ws.addRow(r);
    }
    _styleTemplateWorksheet(ws, licenseNames, sampleRows, bannerRows + 1);
    return wb;
};

/** Trigger a browser download for an exceljs workbook built by
 *  `buildStudentUploadTemplateWorkbook` (or any other exceljs Workbook). */
export const downloadExcelJsWorkbook = async (wb, filename) => {
    const buf = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
    );
};

export const downloadWorkbookXlsx = (wb, filename) => {
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
        new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
    );
};

/**
 * Apply the same worksheet layout used by `buildStudentUploadTemplateWorkbook`:
 *   - auto-fit column widths (capped 14–42 chars, +4 padding)
 *   - uniform row height of 20pt
 * Works on any aoa (array-of-arrays) already written into `ws`.
 * @param {object}   ws      SheetJS worksheet object (mutated in place).
 * @param {string[][]} csvData The same aoa passed to `XLSX.utils.aoa_to_sheet`.
 */
const _applyWorksheetStyles = (ws, csvData) => {
    if (!csvData.length) return;
    const totalCols = Math.max(...csvData.map((r) => r.length));
    ws['!cols'] = Array.from({ length: totalCols }, (_, i) => {
        const maxLen = csvData.reduce(
            (m, r) => Math.max(m, [...String(r[i] ?? '')].length),
            0,
        );
        return { wch: Math.min(Math.max(maxLen + 4, 14), 42) };
    });
    ws['!rows'] = csvData.map(() => ({ hpt: 20 }));
};

const normalizeHeaderKey = (key) => _fromBoldUnicode(String(key ?? ''))
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

/**
 * Map analytics / profiles_data display column titles to `post_user_licenses_service` keys.
 * Headers from our own template may use bold Unicode glyphs (see
 * `_fromBoldUnicode` above) \u2014 normalize those back to plain ASCII first so
 * matching works whether the file's headers were ever edited or not.
 */
const headerToJsonKey = (rawHeader) => {
    const s = _fromBoldUnicode(String(rawHeader ?? '')).trim();
    const sl = s.toLowerCase().replace(/\u2019/g, "'");
    if (!s) return '';
    if (sl === 'first name') return 'first_name';
    if (sl === 'last name') return 'last_name';
    if (sl.includes('birthdate')) return 'birthdate';
    // "Year of Birth (YYYY)" — matches the manual table's own field exactly
    // (a dropdown of years, not a full date). Converted to birthdate afterward
    // (01/01/<year>, the same convention buildInsiderLicensePostDataFromClassTabs
    // already uses for the manual table's Year of Birth field) — must happen
    // before the push filter below, since the backend needs `birthdate`, not
    // this raw column.
    if (sl.includes('year of birth')) return 'year_of_birth_raw';
    if (sl.includes('learner email') || sl === 'email address') return 'email_address';
    // Enroll-mode template only — combined into email_address afterward (student's
    // own email wins, falling back to this), the same way the manual table does.
    if (sl === 'parent email') return 'parent_email_raw';
    if (sl.includes('gender') && sl.includes('male')) return 'gender';
    if (sl === 'postal code') return 'postal_code';
    if (sl === 'city') return 'city';
    // PBDQ-45 — learner country/state, set by the teacher in the CSV.
    if (sl === 'country') return 'country';
    if (sl.startsWith('state')) return 'state';
    if (sl.includes('username') && sl.includes('optional')) return 'username';
    if (sl.includes('password') && sl.includes('optional')) return 'password';
    if (sl.includes('openid')) return 'openid_email';
    if (sl.includes('class/sub-group name') || sl === 'class name') return 'class_name';
    if (sl === 'grade level') return 'grade_level';
    if (sl === 'academic year') return 'academic_year';
    if (sl.includes('school/group level')) return 'school_level';
    if (sl.includes('department')) return 'department';
    if (sl.includes('job title')) return 'job_title';
    if (sl.includes('license') && sl.includes('dqp')) return 'license_type';
    if (sl === 'audience type') return 'user_group';
    if (sl.includes('class/sub-group head first name') || sl === 'teacher first name') return 'incharge_first_name';
    if (sl.includes('class/sub-group head last name') || sl === 'teacher last name') return 'incharge_last_name';
    if (sl.includes('class/sub-group head email') || sl === 'teacher email') return 'incharge_email';
    return normalizeHeaderKey(rawHeader);
};

const isLicenseBlockHeaderRow = (row) => {
    const cells = (row || []).map((c) => _fromBoldUnicode(String(c ?? '')).trim().toLowerCase());
    // "First Name"+"Last Name" appearing anywhere in the row — not at fixed
    // positions — is already a unique, reliable signal on its own: the
    // school/head block's row never contains both (it has no "First Name" at
    // all in its own right, only as part of the head contact's own name
    // columns, which this school/head block was removed entirely anyway).
    // Content-based instead of position-based so a future column reorder
    // (e.g. leading with Class Name/Grade Level/Teacher info, as this
    // template now does) can't silently break detection again.
    return cells.includes('first name') && cells.includes('last name');
};

/**
 * Read the class-info block (Class Name/Grade Level/Academic Year/Teacher
 * fields) that now sits above the student table as its own label+value pair
 * (see buildStudentUploadTemplateWorkbook) instead of repeating on every
 * student row. Returns null when the file has no such block — an older
 * flat-table template (those columns lived per-row instead, and still parse
 * via headerToJsonKey the normal way), or the enroll-mode/employee templates,
 * neither of which ever had one.
 */
const parseClassInfoBlockFromAoa = (aoa) => {
    const norm = (v) => _fromBoldUnicode(String(v ?? '')).trim();
    const normKey = (v) => norm(v).toLowerCase();
    for (let i = 0; i < Math.min(aoa?.length || 0, 10); i += 1) {
        const row = (aoa[i] || []).map(normKey);
        const nameIdx = row.indexOf('class name');
        if (nameIdx === -1) continue;
        const idxOf = (label) => row.indexOf(label);
        const valueRow = aoa[i + 1] || [];
        const get = (idx) => (idx === -1 ? '' : norm(valueRow[idx]));
        const class_name = get(nameIdx);
        if (!class_name) return null;
        return {
            class_name,
            grade_level: get(idxOf('grade level')),
            academic_year: get(idxOf('academic year')),
            incharge_first_name: get(idxOf('teacher first name')),
            incharge_last_name: get(idxOf('teacher last name')),
            incharge_email: get(idxOf('teacher email')),
        };
    }
    return null;
};

/**
 * "Year of Birth (YYYY)" → birthdate as 01/01/<year> (API expects DD/MM/YYYY).
 * Same convention buildInsiderLicensePostDataFromClassTabs already uses for the
 * manual table's Year of Birth field, so both paths produce an identical shape.
 * Range mirrors that function's own validation (min age 8).
 */
const yearOfBirthToBirthdate = (yearOfBirthRaw) => {
    const yobInt = parseInt(yearOfBirthRaw, 10);
    const thisYear = new Date().getFullYear();
    if (!isNaN(yobInt) && yobInt >= 1920 && yobInt <= thisYear - 8) {
        return `01/01/${yobInt}`;
    }
    return null;
};

/**
 * Extract license rows from profiles_data-style sheet (head block + blank rows + license block).
 */
// A class-info label row ("Class Name | Grade Level | …") — never the student
// header, which on an older flat template carries "Class Name" as a column.
const isClassInfoHeaderRow = (row) => {
    const cells = (row || []).map((c) => _fromBoldUnicode(String(c ?? '')).trim().toLowerCase());
    return cells.includes('class name') && !isLicenseBlockHeaderRow(row);
};

/**
 * Keep only the rows of one class, by the class name the file gives them.
 * Used when an Export Class Details file (every class, one under another) is
 * uploaded into a single class (bug sheet LA-SW-12Oct-02).
 */
export const licenseRowsOfClass = (licenses, className, teacherEmail = '') => {
    const key = (v) => String(v || '').trim().toLowerCase();
    const rows = (licenses || []).filter((lic) => key(lic?.class_name) === key(className));
    // A network's export can hold the same class name under two schools — the
    // class's teacher tells them apart.
    const sameTeacher = key(teacherEmail) ? rows.filter((lic) => key(lic?.incharge_email) === key(teacherEmail)) : [];
    return sameTeacher.length ? sameTeacher : rows;
};

const parseLicenseRowsFromAoa = (aoa, meta = null) => {
    let headerIdx = -1;
    for (let i = 0; i < aoa.length; i++) {
        if (isLicenseBlockHeaderRow(aoa[i])) {
            headerIdx = i;
            break;
        }
    }
    if (headerIdx < 0) return null;

    const headerRow = aoa[headerIdx] || [];
    const headers = headerRow.map((h) => String(h ?? '').trim());
    const licenses = [];
    // One class per file — the block (if present) applies to every row that
    // doesn't already carry these fields itself (an older per-row template).
    let classInfo = parseClassInfoBlockFromAoa(aoa);

    for (let i = headerIdx + 1; i < aoa.length; i++) {
        const row = aoa[i];
        if (!row || !row.some((c) => String(c ?? '').trim() !== '')) continue;
        /*
         * Export Class Details lists every class one under another, each with
         * its own class block and student header (LA-SW-12Oct-02). Those rows
         * were read as students — "invalid email ==> Teacher Last Name". A
         * further class block now starts that class; its headings are skipped.
         */
        if (isClassInfoHeaderRow(row)) {
            classInfo = parseClassInfoBlockFromAoa([row, aoa[i + 1] || []]);
            // The export's own label for learners who are in no class.
            if (classInfo?.class_name === '(No class)') classInfo = null;
            if (meta) meta.stackedClasses = true;
            i += 1; // the block's value row
            continue;
        }
        if (isLicenseBlockHeaderRow(row)) continue;
        const obj = {};
        for (let j = 0; j < headers.length; j++) {
            const title = headers[j];
            if (!title) continue;
            const jsonKey = headerToJsonKey(title);
            if (!jsonKey) continue;
            const v = row[j];
            obj[jsonKey] = v != null && typeof v !== 'object' ? String(v).trim() : v;
        }
        if (classInfo) {
            for (const [k, v] of Object.entries(classInfo)) {
                if (!obj[k] && v) obj[k] = v;
            }
        }
        if (obj.email && !obj.email_address) obj.email_address = obj.email;
        // Enroll-mode template's separate "Parent Email" column (parent_email_raw)
        // — combine into email_address the same way the manual enroll table does:
        // student's own email wins, falling back to the parent's. Must happen
        // BEFORE the push filter below, or a row with only a parent email (no
        // learner email — the under-18 case this column exists for) would be
        // dropped as if it had no email at all.
        // The "Learner Email" header maps to email_address by name, but on the
        // backend `email` is the PARENT's address — what the roster labels "Parent
        // Email" and what every consent pathway mails. So the two are swapped here:
        // the parent's becomes email_address, the learner's travels as student_email.
        //
        // This used to collapse both into email_address with the learner's winning,
        // quietly filing a learner address as the parent's. The fallback to the
        // learner's remains so a row with only that one still enrolls, and must
        // happen BEFORE the push filter below or such a row would be dropped as if
        // it had no email at all.
        {
            const learner_raw = String(obj.email_address || '').trim();
            const parent_raw = String(obj.parent_email_raw || '').trim();
            if (learner_raw) obj.student_email = learner_raw;
            obj.email_address = parent_raw || learner_raw;
        }
        delete obj.parent_email_raw;
        if (!obj.birthdate && obj.year_of_birth_raw) {
            const converted = yearOfBirthToBirthdate(obj.year_of_birth_raw);
            if (converted) obj.birthdate = converted;
        }
        delete obj.year_of_birth_raw;
        if (!obj.license_type) obj.license_type = 'DQP';
        if (obj.email_address) licenses.push(obj);
    }
    return licenses;
};

function rowObjectToLicense(row) {
    const lic = {};
    for (const [rawKey, val] of Object.entries(row)) {
        const k = headerToJsonKey(rawKey);
        lic[k] = val != null && typeof val !== 'object' ? String(val).trim() : val;
    }
    if (lic.email && !lic.email_address) lic.email_address = lic.email;
    if (!lic.email_address && lic.parent_email_raw) lic.email_address = lic.parent_email_raw;
    delete lic.parent_email_raw;
    if (!lic.birthdate && lic.year_of_birth_raw) {
        const converted = yearOfBirthToBirthdate(lic.year_of_birth_raw);
        if (converted) lic.birthdate = converted;
    }
    delete lic.year_of_birth_raw;
    if (!lic.license_type) lic.license_type = 'DQP';
    return lic;
}

/**
 * Map the Manage Classes School picker selection to the CSV service's head
 * (institute) block. A new school carries full details (the API get-or-creates
 * the INSTITUTE row from them); an existing school only needs its code.
 * Falls back to the legacy 'default' shell when no school is in play.
 */
const _norm = (v) => _fromBoldUnicode(String(v == null ? '' : v)).trim();
const _normKey = (v) => _norm(v).toLowerCase();

/**
 * Read the school/head block from the top of an uploaded template sheet.
 * Header row: Head Teacher/Group Email | First Name | Last Name | Country |
 * City | Postal Code | School/Group Name — values on the next non-empty row.
 * Returns null for license-only files (no school block).
 */
export const parseSchoolBlockFromAoa = (aoa) => {
    for (let i = 0; i < Math.min(aoa?.length || 0, 10); i += 1) {
        const row = (aoa[i] || []).map(_normKey);
        const emailIdx = row.findIndex((c) => c === 'head teacher/group email');
        if (emailIdx === -1) continue;
        const idxOf = (label) => row.findIndex((c) => c === label);
        const nameIdx = idxOf('school/group name');
        const firstIdx = idxOf('first name');
        const lastIdx = idxOf('last name');
        const countryIdx = idxOf('country');
        const cityIdx = idxOf('city');
        const postalIdx = idxOf('postal code');
        // Values sit on the next non-empty row (before the license header block).
        for (let j = i + 1; j < Math.min(aoa.length, i + 4); j += 1) {
            const vals = aoa[j] || [];
            const get = (idx) => (idx === -1 ? '' : _norm(vals[idx]));
            const headEmail = get(emailIdx);
            const schoolName = get(nameIdx);
            if (!headEmail && !schoolName) continue;
            return {
                schoolName,
                headEmail,
                headFirstName: get(firstIdx),
                headLastName: get(lastIdx),
                headCountry: get(countryIdx),
                headCity: get(cityIdx),
                headPostalCode: get(postalIdx),
            };
        }
        return null;
    }
    return null;
};

/**
 * Resolve a file's school block against the org's saved schools: a School/Group
 * Name or head-email match reuses that school's institute_code; otherwise the
 * block becomes a NEW school (SCH_<uuid>) created by the upload itself.
 */
export const resolveFileSchool = (block, existingSchools = []) => {
    if (!block) return null;
    const nameKey = _normKey(block.schoolName);
    const emailKey = _normKey(block.headEmail);
    const match = (existingSchools || []).find((s) =>
        (nameKey && _normKey(s.institute_name) === nameKey)
        || (emailKey && _normKey(s.institute_head_email) === emailKey));
    if (match) return { institute_code: match.institute_code };
    return { isNew: true, institute_code: genSchoolInstituteCode(), ...block };
};

export const schoolToCsvHead = (school) => {
    /*
     * No school chosen → say NOTHING, and let the server decide which one.
     *
     * This used to answer 'default'. Add a Class no longer asks which school,
     * so every class created with students carried it — and the server reads
     * the head first:
     *
     *   ResolveCode(org_code, head.institute_code || caller_institute_code)
     *
     * so 'default' beat the caller's own school. A school inside a network then
     * filed its class under a school that does not exist, and its own list —
     * which filters to its real institute_code — never showed it. The network
     * owns the code, so ITS list is unfiltered and looked fine, which is why
     * this only ever reproduced on a sub-school.
     *
     * Omitting the key lets caller_institute_code through for a school, and
     * still resolves to 'default' for a network or an older org, so nothing
     * else moves.
     */
    if (!school?.institute_code) return {};
    if (!school.isNew) return { institute_code: school.institute_code };
    return {
        institute_code: school.institute_code,
        institute_name: String(school.schoolName || '').trim(),
        head_email: String(school.headEmail || '').trim(),
        head_first_name: String(school.headFirstName || '').trim(),
        head_last_name: String(school.headLastName || '').trim(),
        head_country: String(school.headCountry || '').trim(),
        head_city: String(school.headCity || '').trim(),
        head_postal_code: String(school.headPostalCode || '').trim(),
    };
};

/**
 * Parse first sheet of an uploaded .xlsx/.xls/.csv into `post_user_licenses_service` `data` shape.
 * `opts.school` (Manage Classes School picker) drives the head/institute block;
 * without it the legacy `default` institute keeps matching older org sections.
 */
/**
 * The class templates of 13 Oct 2026 (one learner per row, the class details on
 * every row), in the same `data` shape the older files produce — one block per
 * school — plus the classes that have no learners yet, which the caller creates
 * on their own.
 *
 * @param {object} opts
 * @param {object[]} [opts.networkSchools]  a network's schools (contact_email,
 *        org_code, institute_code, org_name): School Email picks one of them.
 */
const classImportToPostData = (wb, found, opts = {}) => {
    const { classes, problems } = readClassImportRows(wb, found);
    const fail = (list) => ({
        error: `${list[0]}${list.length > 1 ? ` (+${list.length - 1} more problem${list.length > 2 ? 's' : ''})` : ''}`,
        problems: list,
        data: null,
    });
    if (problems.length) return fail(problems);

    const network = found.kind === TEMPLATE_NETWORK_CLASS;
    const lower = (v) => String(v || '').trim().toLowerCase();
    const groups = new Map();
    const more = [];
    classes.forEach((c) => {
        let school = null;
        if (network) {
            school = (opts.networkSchools || []).find((s) => lower(s.contact_email) === lower(c.school_email)) || null;
            if (!school) {
                more.push(`Row ${c._row}, School Email: no school in your network has the Contact Email "${c.school_email}" — use the school's Contact Email exactly as it is on My Schools`);
                return;
            }
            // A school picked in the dialog is where the classes go, as on Add a Class.
            const picked = opts.pickedSchool;
            if (picked && String(school.institute_code || '') !== String(picked.institute_code || '')) {
                more.push(`Row ${c._row}, School Email: "${c.school_email}" is ${school.org_name || 'another school'}, but ${picked.org_name || 'another school'} is picked under Select a school — use ${picked.org_name || 'that school'}'s Contact Email, or for several schools in one file set My Classes to "All schools in the network" and add the class again`);
                return;
            }
        }
        const gKey = school ? `${school.org_code}::${school.institute_code || ''}` : 'own';
        if (!groups.has(gKey)) groups.set(gKey, { school, license: [], empty: [] });
        const g = groups.get(gKey);
        const classFields = {
            class_name: c.class_name,
            grade_level: c.grade_level,
            school_level: c.grade_level,
            academic_year: c.academic_year,
            incharge_first_name: c.teacher_first_name,
            incharge_last_name: c.teacher_last_name,
            incharge_email: c.teacher_email,
            section_code: genSectionCode(),
            license_type: 'DQP',
        };
        if (!c.learners.length) {
            g.empty.push(classFields);
            return;
        }
        c.learners.forEach((l) => {
            const birthdate = yearOfBirthToBirthdate(l.year_of_birth);
            if (!birthdate) more.push(`Row ${l._row}, Year of Birth: ${l.year_of_birth} — the learner must be 8 or older`);
            g.license.push({
                ...classFields,
                first_name: l.first_name,
                last_name: l.last_name,
                birthdate,
                // The API's email_address is the PARENT's; the learner's travels as
                // student_email. The Guide puts the parent's email in Learner Email
                // when the learner has none — that is not the learner's own address.
                email_address: l.parent_email || l.learner_email,
                student_email: String(l.learner_email).toLowerCase() === String(l.parent_email).toLowerCase() ? '' : l.learner_email,
                gender: l.gender,
                country: l.country,
                state: l.state,
                city: l.city,
                postal_code: l.postal_code,
            });
        });
    });
    if (more.length) return fail(more);

    const all = [...groups.values()];
    const own = opts.forceSchool || opts.school || null;
    return {
        error: null,
        importTemplate: found.kind,
        // More than one class in the file — Manage Students then takes only its own class's learners.
        stackedClasses: classes.length > 1,
        data: all.filter((g) => g.license.length).map((g) => ({
            head: [schoolToCsvHead(g.school ? { institute_code: g.school.institute_code } : own)],
            license: g.license,
        })),
        emptyClasses: all.filter((g) => g.empty.length).map((g) => ({ school: g.school, classes: g.empty })),
    };
};

/**
 * Export Class Details in the class import template itself (Ian, 13 Oct 2026:
 * one file for importing and exporting): one row per learner, the class details
 * on every row, and one row for each class that has no learners yet.
 *
 * @param {object}   p
 * @param {boolean}  p.network          the network file (School Email first)
 * @param {object[]} p.sheetsData       analyticsGetUsersCsvService blocks
 * @param {object}   p.classNameByCode  section code → class name
 * @param {object}   [p.schoolEmailByCode] institute code → the school's Contact Email
 * @param {object[]} [p.sections]       every class (sections list rows), so the empty ones are kept
 * @returns {Promise<Blob>}
 */
export const buildClassImportExportBlob = async ({
    network, sheetsData = [], classNameByCode = {}, schoolEmailByCode = {}, sections = [],
}) => {
    const kind = network ? TEMPLATE_NETWORK_CLASS : TEMPLATE_SCHOOL_CLASS;
    const lookup = await loadTemplateLookup(kind);
    const cap = (v) => { const s = String(v || '').trim(); return s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : ''; };
    const rows = [];
    const withLearners = new Set();
    (sheetsData || []).forEach((block) => {
        const inst = String(block.head?.institute_code || '').trim();
        (block.license || []).forEach((lic) => {
            const code = String(lic.class_name || '').trim();
            withLearners.add(code);
            const parent = String(lic.email_address || '').trim();
            const learner = String(lic.student_email || '').trim();
            rows.push({
                school_email: network ? (schoolEmailByCode[inst] || '') : '',
                class_name: classNameByCode[code] || code,
                grade_level: lic.grade_level || lic.school_level || '',
                academic_year: lic.academic_year || '',
                teacher_first_name: lic.incharge_first_name || '',
                teacher_last_name: lic.incharge_last_name || '',
                teacher_email: lic.incharge_email || '',
                learner_first_name: lic.first_name || '',
                learner_last_name: lic.last_name || '',
                // Stored as 01/01/<year>: the year is its only information.
                year_of_birth: String(lic.birthdate || '').split('/')[2] || '',
                // The Guide: a learner with no email of their own carries the parent's.
                learner_email: learner || parent,
                parent_email: parent,
                gender: cap(lic.gender),
                country: countryNameFor(lookup, lic.country),
                state: stateForExport(lookup, lic.country, lic.state),
                city: lic.city || '',
                postal_code: lic.postal_code || '',
            });
        });
    });
    // A class with no learners yet: one row, learner columns empty (the Guide's own rule).
    (sections || []).forEach((s) => {
        const code = String(s.section_code || '').trim();
        if (!code || withLearners.has(code)) return;
        const names = String(s.section_incharge_name || '').trim().split(/\s+/).filter(Boolean);
        rows.push({
            school_email: network ? (schoolEmailByCode[String(s.institute_code || '').trim()] || '') : '',
            class_name: s.section_name || code,
            grade_level: s.section_level || '',
            academic_year: s.academic_year || '',
            teacher_first_name: names[0] || '',
            teacher_last_name: names.slice(1).join(' '),
            teacher_email: s.section_incharge_email || '',
        });
    });
    const by = (r) => `${r.school_email}\u0000${r.class_name}`.toLowerCase();
    rows.sort((a, b) => (by(a) < by(b) ? -1 : by(a) > by(b) ? 1 : 0));
    return fillImportTemplate(kind, rows);
};

/** Which of the four import templates a file is, or null. */
export const importTemplateKindOfFile = async (file) => {
    const found = detectImportTemplate(XLSX.read(await file.arrayBuffer(), { type: 'array' }));
    return found ? found.kind : null;
};

export const parseStudentLicenseWorkbookToPostData = async (file, orgType, opts = {}) => {
    void orgType;
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    // The import templates of 13 Oct 2026; any other file is read as before.
    const importTemplate = detectImportTemplate(wb);
    if (importTemplate && [TEMPLATE_SCHOOL_CLASS, TEMPLATE_NETWORK_CLASS].includes(importTemplate.kind)) {
        return classImportToPostData(wb, importTemplate, opts);
    }
    if (importTemplate) {
        return { error: 'This is the schools template — upload it from My Schools. Classes take the classes and learners template.', data: null };
    }
    const sheetName = wb.SheetNames[0];
    if (!sheetName) {
        return { error: 'The file has no sheets.', data: null };
    }
    const sheet = wb.Sheets[sheetName];
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const meta = {};
    let licenses = parseLicenseRowsFromAoa(aoa, meta);
    // The approved template names its sheet "Class 1": a file may carry one
    // class per sheet ("Class 2", …). Every further sheet with a student table
    // is read the same way, each with its own class block; a sheet without one
    // (notes, a blank sheet) is skipped. The school block below is still read
    // from the first sheet only.
    for (const name of wb.SheetNames.slice(1)) {
        const more = parseLicenseRowsFromAoa(XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' }), meta);
        if (more && more.length) licenses = [...(licenses || []), ...more];
    }

    if (!licenses || licenses.length === 0) {
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        if (!rows.length) {
            return { error: 'No data rows under the header row.', data: null };
        }
        licenses = [];
        for (const row of rows) {
            const lic = rowObjectToLicense(row);
            if (lic.email_address) licenses.push(lic);
        }
    }

    if (!licenses.length) {
        return { error: 'Add at least one row with email_address filled in.', data: null };
    }

    // Assign ONE random section_code per class name in the file (shared by that
    // class's students). The API prefers an existing class's stored code (matched
    // by name), so re-uploading an existing class keeps its code.
    const _codeByClass = {};
    for (const lic of licenses) {
        const cn = String(lic.class_name || '').trim();
        if (!cn) continue;
        if (!_codeByClass[cn]) _codeByClass[cn] = genSectionCode();
        lic.section_code = _codeByClass[cn];
    }

    // School priority (highest first):
    //  1. opts.forceSchool — enroll: the class is locked to its own school.
    //  2. opts.school      — the dialog's School picker selection. When the user
    //     PICKS a school and also uploads a file, the picked school WINS over any
    //     school block inside the file (per requirement).
    //  3. the file's own school block (matched to an existing school by name/head
    //     email, or created as a new one) — used only when nothing was picked.
    //  4. legacy 'default' shell (schoolToCsvHead handles the null case).
    const resolvedSchool = opts.forceSchool
        || opts.school
        || resolveFileSchool(parseSchoolBlockFromAoa(aoa), opts.schools);
    const data = [
        {
            head: [schoolToCsvHead(resolvedSchool)],
            license: licenses,
        },
    ];
    // stackedClasses: the file is an export of several classes, not a template.
    return { error: null, data, stackedClasses: !!meta.stackedClasses };
};

/**
 * @param {object[]} classTabs
 * @param {{ defaultUserGroup?: string }} [opts] 
 */
export const buildInsiderLicensePostDataFromClassTabs = (classTabs, opts = {}) => {
    const defaultUserGroup = String(opts.defaultUserGroup || '').trim();
    const licenses = [];
    for (const tab of classTabs || []) {
        const class_name = String(tab.className || '').trim();
        if (!class_name) continue;
        // One code per class (shared by all its students). Editing an existing
        // class carries its own code (tab.sectionCode); a NEW class gets a fresh
        // SEC_xxxx. The API still matches by NAME, so an existing class keeps its
        // stored code regardless of what we send here.
        const section_code = String(tab.sectionCode || '').trim() || genSectionCode();
        const grade_level = String(tab.gradeLevel || '').trim();
        const academic_year = String(tab.academicYear || '').trim();
        const incharge_first_name = String(tab.teacherFirstName || '').trim();
        const incharge_last_name = String(tab.teacherLastName || '').trim();
        const incharge_email = String(tab.teacherEmail || '').trim();
        // Class-level default — derived/persisted on the class tab itself (see
        // AddClassStudentsDialog's enrollDerived logic). A student row's own
        // Postal Code input (added alongside Gender/Country/State) overrides
        // this per-row when the teacher fills it in.
        const classPostalCode = String(tab.schoolPostalCode || '').trim();
        const prefix = String(tab.userPrefix || '').trim();
        // Enroll mode: continue numbering AFTER the class's existing students.
        // Regenerating an existing username would hit the CSV service's same-org
        // reuse path and silently overwrite that student instead of adding one.
        // Belt-and-suspenders: skip any number whose username is already present
        // in this class's roster (existingUsernames), so a mis-derived offset can
        // never reuse an existing learner's username.
        const usernameOffset = Number(tab.usernameStartOffset) || 0;
        const usedUsernames = new Set(
            (tab.existingUsernames || []).map((u) => String(u || '').trim().toLowerCase()),
        );
        let nextUsernameNum = usernameOffset + 1;
        for (const s of tab.students || []) {
            // `email_address` becomes `profile.email` on the backend, which is the
            // PARENT's address — the roster labels it "Parent Email" and it is where
            // every consent pathway's mail goes. So the parent's is preferred here,
            // and the learner's own travels separately as `student_email`.
            //
            // This used to prefer the STUDENT's, which filed a learner address in a
            // field labelled and mailed as the parent's for every row carrying both.
            // The fallback to the learner's is kept only so a row carrying just that
            // one still enrolls — it is the sole address available, and dropping the
            // student would be worse.
            const parent_email = String(s.parentEmail || '').trim();
            const student_email = String(s.studentEmail || '').trim();
            const email_address = parent_email || student_email;
            if (!email_address) continue;
            const lic = {
                class_name,
                section_code, // random per-class code; API prefers an existing class's stored code
                grade_level,
                school_level: grade_level,
                first_name: String(s.firstName || '').trim(),
                last_name: String(s.lastName || '').trim(),
                email_address,
                // Omitted rather than sent empty when the learner has no address of
                // their own — consent Case 3 is exactly that, and the backend treats
                // a missing column as "leave alone" rather than "clear".
                ...(student_email ? { student_email } : {}),
                license_type: 'DQP',
                incharge_first_name,
                incharge_last_name,
                incharge_email,
                postal_code: classPostalCode,
            };
            if (academic_year) lic.academic_year = academic_year;
            // Optional — same as the upload template's Gender/Country/State/
            // Postal Code columns, left off (or left at the class default, for
            // postal code) rather than sent as empty strings.
            if (String(s.gender || '').trim()) lic.gender = String(s.gender).trim();
            if (String(s.city || '').trim()) lic.city = String(s.city).trim();
            if (String(s.country || '').trim()) lic.country = String(s.country).trim();
            if (String(s.state || '').trim()) lic.state = String(s.state).trim();
            if (String(s.postalCode || '').trim()) lic.postal_code = String(s.postalCode).trim();
            // Year of birth → birthdate as 01/01/<year> (API expects DD/MM/YYYY).
            // Same shape the demographic form produces (01/01/<year> → dob_datetime),
            // so the API can derive year_of_birth back from it. Range mirrors the
            // demographic form's validation: 1920 .. current year - 8 (min age 8).
            const yobInt = parseInt(s.yearOfBirth, 10);
            const thisYear = new Date().getFullYear();
            if (!isNaN(yobInt) && yobInt >= 1920 && yobInt <= thisYear - 8) {
                lic.birthdate = `01/01/${yobInt}`;
            }
            if (defaultUserGroup) {
                lic.user_group = defaultUserGroup;
            }
            if (prefix) {
                while (usedUsernames.has(`${prefix}${nextUsernameNum}`.toLowerCase())) {
                    nextUsernameNum += 1;
                }
                lic.username = `${prefix}${nextUsernameNum}`;
                // Reserve it so multiple new students in this batch don't clash.
                usedUsernames.add(lic.username.toLowerCase());
                nextUsernameNum += 1;
            }
            licenses.push(lic);
        }
    }
    if (!licenses.length) {
        return { error: null, data: null };
    }
    return {
        error: null,
        data: [{ head: [schoolToCsvHead(opts.school)], license: licenses }],
    };
};

export const splitClassTabsForManageClassesSave = (classTabs) => {
    const shellTabs = [];
    const enrollTabs = [];
    for (const tab of classTabs || []) {
        const className = String(tab.className || '').trim();
        if (!className) continue;
        const hasStudentEmail = (tab.students || []).some((s) =>
            String(s.studentEmail || s.parentEmail || '').trim(),
        );
        if (hasStudentEmail) {
            enrollTabs.push(tab);
        } else {
            shellTabs.push(tab);
        }
    }
    return { shellTabs, enrollTabs };
};

/** Grade level required for CSV path (creates section via redundant_sections). */
export const validateManualEnrollTabsForCsvPost = (enrollTabs) => {
    for (const tab of enrollTabs) {
        const className = String(tab.className || '').trim();
        if (!String(tab.gradeLevel || '').trim()) {
            return { ok: false, message: `Grade level is required for class "${className}" (same as CSV upload).` };
        }
        const hasEmail = (tab.students || []).some((s) =>
            String(s.studentEmail || s.parentEmail || '').trim(),
        );
        if (!hasEmail) {
            return {
                ok: false,
                message: `Add at least one student or parent email for class "${className}".`,
            };
        }
        if (!String(tab.teacherEmail || '').trim()) {
            return {
                ok: false,
                message: `Teacher email is required for class "${className}" (same as CSV upload / teacher account).`,
            };
        }
    }
    return { ok: true };
};

// School info (Head Teacher/Group Email, School/Group Name, ...) was removed
// entirely from this export per request — this file is always downloaded
// from within one specific school's own "Manage Classes" page, so repeating
// its own school's contact details back to it added nothing.
//
// The export now mirrors the upload template's own class-info/student-info
// split (see CLASS_INFO_COLUMN_NAMES / STUDENT_COLUMN_NAMES): rather than one
// flat table with class/teacher fields repeated on every single row, each
// class gets its own class-info block followed by just that class's student
// rows (see buildExistingUsersWorkbook) — a school's roster can span many
// classes per sheet, so this repeats once per class instead of once per file.
const CLASS_INFO_EXPORT_COLS = [
    { name: 'Class Name',           key: 'class_name' },
    { name: 'Grade Level',          key: 'grade_level' },
    { name: 'Academic Year',        key: 'academic_year' },
    { name: 'Teacher First Name',   key: 'incharge_first_name' },
    { name: 'Teacher Last Name',    key: 'incharge_last_name' },
    { name: 'Teacher Email',        key: 'incharge_email' },
];

// "Year of Birth (YYYY)" is presented from the stored `birthdate` the same
// way the upload template does. That's lossless, not lossy: every stored
// birthdate here is always `01/01/<year>` (see
// buildInsiderLicensePostDataFromClassTabs / yearOfBirthToBirthdate) — the
// day/month are placeholder, so the year is the only real information in it.
// The two emails are separate fields on the account (LA-SW-07-Oct-02):
// `student_email` is the learner's own, `email_address` (profile.email) is the
// parent's — the one the consent mails go to. A learner entered with only
// their own address has it in both; it is then shown once, as the learner's.
const STUDENT_EXPORT_COLS = [
    { name: 'First Name',                                          key: 'first_name' },
    { name: 'Last Name',                                           key: 'last_name' },
    { name: 'Year of Birth (YYYY)',                                key: 'year_of_birth' },
    { name: "Learner Email (the student's own — leave blank if none)", key: 'student_email' },
    { name: 'Parent Email',                                        key: 'email_address' },
    { name: 'Gender (Male /Female /Other)',                        key: 'gender' },
    { name: 'Postal Code',                                         key: 'postal_code' },
    { name: 'Country',                                             key: 'country' },
    { name: 'State',                                               key: 'state' },
];

const COMPANY_LICENSE_EXPORT_COLS = [
    { name: 'First Name',                               key: 'first_name' },
    { name: 'Last Name',                                key: 'last_name' },
    { name: 'Department',                               key: 'department' },
    { name: 'Job Title',                                key: 'job_title' },
    { name: 'Birthdate (DD/MM/YYYY)',                   key: 'birthdate' },
    { name: 'Email Address',                            key: 'email_address' },
    { name: 'Gender (Male /Female /Other)',             key: 'gender' },
    { name: 'Username (Optional / Required to Update)', key: 'username' },
];

/**
 * Returns the json_key array to pass to `analyticsGetUsersCsvService`. Two of
 * the school display columns don't map 1:1 to a real backend field:
 * 'year_of_birth' is derived client-side from the real 'birthdate' field (so
 * 'birthdate' is what's actually requested).
 */
export const getExistingUsersExportKeys = (orgType) =>
    (orgType === ORG_TYPE_COMPANY ? COMPANY_LICENSE_EXPORT_COLS : [...CLASS_INFO_EXPORT_COLS, ...STUDENT_EXPORT_COLS])
        .map((c) => (c.key === 'year_of_birth' ? 'birthdate' : c.key))
        .filter(Boolean);

/** Excel tab names can't exceed 31 chars or contain : \ / ? * [ ] — sanitize. */
const _safeSheetName = (raw, fallback) => {
    const cleaned = String(raw || '').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31);
    return cleaned || fallback;
};

/**
 * Build a downloadable workbook from `analyticsGetUsersCsvService` response.
 * Produces the same class-info-block + student-table layout as the upload
 * template so the file can be re-uploaded directly after editing.
 *
 * The backend returns CODES (institute_code `SCH_…`, section_code `SEC_…`) in the
 * "School/Group Name" and "Class/Sub-Group Name" columns. The upload template and
 * its parser use NAMES there (school/class are matched by name), so we translate
 * codes → names via `nameMaps` before writing. Every lookup falls back to the raw
 * value, so an unmapped code is written unchanged — never lost — and the file still
 * re-uploads correctly (name match for known classes, code passthrough otherwise).
 */
export const buildExistingUsersWorkbook = async (sheetsData, orgType, nameMaps = {}) => {
    const schoolNameByCode = nameMaps.schoolNameByCode || {};
    const classNameByCode = nameMaps.classNameByCode || {};
    const isSchool = orgType !== ORG_TYPE_COMPANY;
    const wb = new ExcelJS.Workbook();
    const logoBuf = await _getLogoBuffer();
    const logoImageId = logoBuf ? wb.addImage({ buffer: logoBuf, extension: 'png' }) : null;
    const usedSheetNames = new Set();

    // Set every column's width by scanning what actually ended up in it — the
    // class-info block and the (repeated, one-per-class) student table share
    // the same columns for different fields, so this can't be sized from a
    // single fixed header/example-row list the way _styleTemplateWorksheet does.
    // Starts AFTER the banner (row 4) — the banner's title is one merged cell
    // spanning every column, and exceljs's eachCell/getCell reports that same
    // merged value for every column in its row, which would otherwise inflate
    // every single column to the width cap.
    const _autoFitColumns = (ws, maxColCount) => {
        for (let i = 1; i <= maxColCount; i += 1) {
            let maxLen = 0;
            for (let r = 4; r <= ws.rowCount; r += 1) {
                const v = ws.getRow(r).getCell(i).value;
                if (v != null) maxLen = Math.max(maxLen, String(v).length);
            }
            ws.getColumn(i).width = Math.min(Math.max(maxLen + 4, 14), 42);
        }
    };

    for (const block of (sheetsData || [])) {
        const instCode = String(block.head?.institute_code || '').trim();
        const schoolName = schoolNameByCode[instCode] || block.head?.institute_name || instCode;

        // Guarantee a unique tab name; names can collide since tabs are now
        // labeled by school name rather than unique code.
        let sheetName = _safeSheetName(schoolName, instCode || 'Licenses');
        if (usedSheetNames.has(sheetName)) {
            const base = sheetName.slice(0, 27);
            let n = 2;
            while (usedSheetNames.has(`${base} (${n})`)) n += 1;
            sheetName = `${base} (${n})`;
        }
        usedSheetNames.add(sheetName);

        const ws = wb.addWorksheet(sheetName);

        if (!isSchool) {
            // Company/employee export — no "class" concept to split out, stays
            // one flat table like before.
            const licCols = COMPANY_LICENSE_EXPORT_COLS;
            const bannerRows = _addBrandBanner(
                wb, ws, `DQ — ${schoolName || 'Company'} — Existing Employees`, licCols.length, logoImageId,
            );
            const tableHeaderRow = bannerRows + 1;
            ws.addRow(licCols.map((c) => c.name));
            const dataRows = [];
            for (const lic of (block.license || [])) {
                const row = licCols.map((c) => (c.key == null ? '' : lic[c.key] ?? ''));
                ws.addRow(row);
                dataRows.push(row);
            }
            _styleTemplateWorksheet(ws, licCols.map((c) => c.name), [], tableHeaderRow);
            ws.columns.forEach((col, i) => {
                let maxLen = String(licCols[i]?.name ?? '').length;
                for (const r of dataRows) maxLen = Math.max(maxLen, String(r[i] ?? '').length);
                col.width = Math.min(Math.max(maxLen + 4, 14), 42);
            });
            _styleDataRows(ws, tableHeaderRow + 1, dataRows.length);
            continue;
        }

        // School export — group this school's students by class. Each class
        // gets its own class-info block (Class Name/Grade Level/Academic Year/
        // Teacher fields) followed by just that class's student rows, instead
        // of one flat table repeating the class/teacher fields on every row.
        const classGroups = new Map();
        for (const lic of (block.license || [])) {
            const code = String(lic.class_name || '').trim();
            const label = classNameByCode[code] || lic.class_name || code || '(No class)';
            if (!classGroups.has(label)) classGroups.set(label, []);
            classGroups.get(label).push(lic);
        }

        const totalCols = Math.max(CLASS_INFO_EXPORT_COLS.length, STUDENT_EXPORT_COLS.length);
        _addBrandBanner(wb, ws, `DQ — ${schoolName || 'School'} — Existing Students`, totalCols, logoImageId);

        for (const [label, licenses] of classGroups) {
            const first = licenses[0] || {};
            const labelRow = ws.addRow(CLASS_INFO_EXPORT_COLS.map((c) => c.name));
            ws.addRow(CLASS_INFO_EXPORT_COLS.map((c) => (
                c.key === 'class_name' ? label : (first[c.key] ?? '')
            )));
            _styleHeadInfoSection(ws, labelRow.number, false);

            ws.addRow([]); // spacer

            const headerRow = ws.addRow(STUDENT_EXPORT_COLS.map((c) => c.name));
            _styleNavyHeaderRow(ws, headerRow.number);
            let dataRowCount = 0;
            for (const lic of licenses) {
                const row = STUDENT_EXPORT_COLS.map((c) => {
                    if (c.key === 'year_of_birth') {
                        // Stored birthdate is always 01/01/<year> — the year
                        // is its only real information (see the field's own
                        // comment above STUDENT_EXPORT_COLS).
                        const parts = String(lic.birthdate || '').split('/');
                        return parts[2] || '';
                    }
                    if (c.key == null) return '';
                    if (c.key === 'email_address') {
                        // Parent Email — blank when the only address on file is the learner's own.
                        const parent = String(lic.email_address || '').trim();
                        const learner = String(lic.student_email || '').trim();
                        return parent && parent.toLowerCase() === learner.toLowerCase() ? '' : parent;
                    }
                    return lic[c.key] ?? '';
                });
                ws.addRow(row);
                dataRowCount += 1;
            }
            _styleDataRows(ws, headerRow.number + 1, dataRowCount);

            ws.addRow([]); // gap before the next class's block
        }

        _autoFitColumns(ws, totalCols);
        ws.views = [{ state: 'frozen', ySplit: 3 }]; // keep the letterhead visible; headers repeat per class
    }
    return wb;
};

/**
 * Flatten `get_user_licenses_service` response (`[{ head, license }]`) into worksheet rows.
 */
export const buildExportWorkbookFromGetUsersPayload = (finalArray, orgType) => {
    const keys = getExportColumnKeys(orgType);
    const flat = [];
    if (!Array.isArray(finalArray)) {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([keys]);
        // Apply column widths and row height to match the upload template layout.
        _applyWorksheetStyles(ws, [keys]);
        XLSX.utils.book_append_sheet(wb, ws, 'Students');
        return wb;
    }
    for (const block of finalArray) {
        const head = block.head && typeof block.head === 'object' ? block.head : {};
        const list = Array.isArray(block.license) ? block.license : [];
        for (const lic of list) {
            const row = {};
            for (const k of keys) {
                row[k] = lic[k] != null ? lic[k] : '';
            }
            row.institute_code = head.institute_code || '';
            row.head_email = head.head_email || '';
            flat.push(row);
        }
    }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(flat);
    // Derive aoa from flat for accurate column-width calculation.
    const aoa = [Object.keys(flat[0] || {}), ...flat.map((r) => Object.values(r))];
    _applyWorksheetStyles(ws, aoa);
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    return wb;
};


/**
 * The template building blocks and the class-sheet readers, shared with the
 * schools / networks upload (schoolsUploadExcel.js) so every downloaded
 * template looks the same and a class sheet is read by one implementation.
 */
export {
    _getLogoBuffer as getTemplateLogoBuffer,
    _addBrandBanner as addTemplateBrandBanner,
    _styleNavyHeaderRow as styleTemplateHeaderRow,
    parseClassInfoBlockFromAoa,
    parseLicenseRowsFromAoa,
    CLASS_INFO_COLUMN_NAMES,
    CLASS_INFO_EXAMPLE_ROW,
    STUDENT_COLUMN_NAMES,
    STUDENT_EXAMPLE_V2 as STUDENT_EXAMPLE_VALUES,
    CLASS_SHEET_NAME,
};
